/**
 * Where to go after signing in, when the visitor was sent to sign in from
 * somewhere specific — a guest pressing "Sign in to pay" at checkout.
 *
 * Session-scoped and short-lived, so a path remembered and abandoned cannot
 * hijack an unrelated sign-in days later. Only same-site paths are honoured.
 */
const KEY = 'masertak.return_path';
const MAX_AGE_MS = 60 * 60 * 1000;

export function rememberReturnPath(path: string) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({path, at: Date.now()}));
  } catch {
    // Storage blocked: the visitor lands on the default page instead.
  }
}

const safe = (path: unknown): path is string =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');

export function readReturnPath(): string | null {
  // A link that says where to come back to (`/login?redirect=/services`,
  // `?redirectTo=`) wins. Those links existed but nothing read them, so the
  // visitor always landed on the default page.
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('redirect') || params.get('redirectTo');
    if (safe(fromUrl)) return fromUrl;
  } catch {
    // no window / bad query
  }
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const {path, at} = JSON.parse(raw);
    if (!safe(path)) return null;
    if (Date.now() - Number(at) > MAX_AGE_MS) return null;
    return path;
  } catch {
    return null;
  }
}

export function clearReturnPath() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // nothing to clear
  }
}
